/* eslint-disable react/no-unescaped-entities */
/**
 * NegociationInvitation.jsx — Vue invité (sans authentification, token URL).
 *
 * Permet à la partie destinataire de consulter le dossier, répondre, contre-
 * proposer et déposer des pièces.
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`).
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConfilexMark } from "@/components/ConfilexLogo";
import { toast } from "sonner";
import axios from "axios";
import {
  Send, Paperclip, FileDown, Loader2, ShieldCheck, Clock, RefreshCw,
  Sparkles, MessageSquare, Upload, Handshake, ArrowRight,
} from "lucide-react";

const COMPLIANCE = (
  "Confilex est un outil de structuration documentaire et de formalisation " +
  "d'informations fournies par les utilisateurs. Confilex ne fournit aucune " +
  "consultation juridique, n'analyse pas les droits des parties, ne vérifie " +
  "pas la validité des créances et ne rédige aucun acte juridique."
);

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NegociationInvitation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [proposal, setProposal] = useState({ amount_eur: "", schedule: "", declared_conditions: "" });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/negotiation/invitation/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Lien d'invitation introuvable ou expiré");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/negotiation/invitation/${token}/messages`, { body: msgBody.trim() });
      setMsgBody("");
      await load();
      toast.success("Message envoyé");
    } catch (e) {
      toast.error("Envoi impossible", { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const sendProposal = async () => {
    if (!proposal.declared_conditions.trim() || proposal.declared_conditions.trim().length < 4) {
      toast.error("Conditions déclarées trop courtes");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/negotiation/invitation/${token}/proposals`, {
        amount_eur: proposal.amount_eur === "" ? null : Number(proposal.amount_eur),
        schedule: proposal.schedule || null,
        declared_conditions: proposal.declared_conditions.trim(),
      });
      setProposal({ amount_eur: "", schedule: "", declared_conditions: "" });
      toast.success("Contre-proposition formalisée");
      await load();
    } catch (e) {
      toast.error("Envoi impossible", { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await axios.post(`${API_BASE}/negotiation/invitation/${token}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce déposée");
      await load();
    } catch (err) {
      toast.error("Upload impossible", { description: err?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API_BASE}/negotiation/invitation/${token}/synthese.pdf`,
        { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = "synthese-conditions-declarees.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  if (loading) return <FullPageLoader />;
  if (error) return <ErrorState message={error} />;

  const { case: c, proposals, messages, attachments } = data;
  const timeline = [
    ...proposals.map(p => ({ kind: "proposal", at: p.created_at, payload: p })),
    ...messages.map(m => ({ kind: "message",  at: m.created_at, payload: m })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header minimaliste sans navbar (invité) */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ConfilexMark />
            <span className="font-serif text-lg text-[#0B3A2A] italic">Confilex Négociation</span>
          </Link>
          <Link to="/register" className="text-xs text-[#0B3A2A] hover:text-[#C9A961] hidden sm:inline" data-testid="invitation-register">
            Créer un compte CONFILEX <ArrowRight className="inline w-3 h-3 ml-0.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="negociation-invitation">
        {/* HERO INVITATION */}
        <section className="rounded-2xl bg-white border border-slate-200 p-7 mb-6">
          <div className="flex items-center gap-2 text-[#C9A961] mb-2">
            <Handshake className="w-5 h-5" />
            <p className="text-[11px] uppercase tracking-[0.3em] font-medium">Une démarche amiable vous est adressée</p>
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#0B3A2A] tracking-tight" data-testid="invitation-subject">
            {c.subject}
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            <b>{c.initiator_display_name}</b> souhaite engager avec vous une démarche
            constructive et amiable. Une proposition a été déposée afin de faciliter les échanges
            et rechercher une solution mutuellement acceptable.
          </p>
          {c.description && (
            <div className="mt-4 p-4 rounded-lg bg-[#FAFAF7] border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
              {c.description}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
            {c.amount_eur != null && (
              <span>Montant déclaré : <b className="text-[#0B3A2A]">
                {Number(c.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
              </b></span>
            )}
            {c.requested_schedule && <span>Échéancier proposé : <b className="text-[#0B3A2A]">{c.requested_schedule}</b></span>}
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FAF7F1] border border-[#C9A961]/30" data-testid="invitation-status">
              {c.status_icon} {c.status_label}
            </span>
          </div>
        </section>

        {/* Découverte CONFILEX */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] text-white p-6 mb-6" data-testid="discovery-block">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">Découvrir CONFILEX</p>
          <p className="text-sm leading-relaxed text-white/90">
            CONFILEX est une plateforme conçue pour les entreprises, professionnels et particuliers
            souhaitant <b>structurer leurs échanges</b>, <b>centraliser leurs documents</b> et favoriser
            la recherche de solutions amiables. Historique horodaté, dépôt documentaire, suivi des
            échanges, export PDF, espace sécurisé, traçabilité complète.
          </p>
          <p className="text-xs text-white/70 italic mt-3">
            Si vous n'êtes pas encore utilisateur de CONFILEX, nous serions ravis de vous accueillir
            à cette occasion.
          </p>
          <div className="mt-4">
            <Button asChild size="sm" className="bg-[#C9A961] hover:bg-[#D4B478] text-[#0B3A2A]" data-testid="discovery-cta">
              <Link to="/register">Créer un compte</Link>
            </Button>
          </div>
        </section>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg text-[#0B3A2A]">Chronologie horodatée</h2>
              <Button variant="outline" size="sm" onClick={load} disabled={busy}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
              </Button>
            </div>
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun échange pour le moment.</p>
            ) : (
              <ul className="space-y-4" data-testid="invitation-timeline">
                {timeline.map((t, idx) => <TimelineItem key={idx} item={t} />)}
              </ul>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Formuler une contre-proposition
              </p>
              <input type="number" min="0" step="0.01" placeholder="Montant (€)"
                value={proposal.amount_eur}
                onChange={e => setProposal(p => ({ ...p, amount_eur: e.target.value }))}
                data-testid="counter-amount"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2" />
              <input placeholder="Échéancier proposé"
                value={proposal.schedule}
                onChange={e => setProposal(p => ({ ...p, schedule: e.target.value }))}
                data-testid="counter-schedule"
                maxLength={600}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2" />
              <textarea placeholder="Conditions déclarées (texte libre)"
                value={proposal.declared_conditions}
                onChange={e => setProposal(p => ({ ...p, declared_conditions: e.target.value }))}
                data-testid="counter-conditions"
                rows={4} maxLength={4000}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2 resize-y" />
              <Button onClick={sendProposal} disabled={busy} size="sm"
                className="w-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-counter">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Envoyer la contre-proposition
              </Button>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Message
              </p>
              <textarea value={msgBody} onChange={e => setMsgBody(e.target.value)}
                data-testid="invitation-message-body" rows={4} maxLength={4000}
                placeholder="Saisissez votre réponse à la partie initiatrice."
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2 resize-y" />
              <Button onClick={sendMessage} disabled={busy || !msgBody.trim()} size="sm"
                className="w-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="invitation-btn-message">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Envoyer le message
              </Button>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Pièces du dossier
              </p>
              <ul className="space-y-2 text-xs mb-3">
                {attachments.length === 0 && <li className="text-slate-400 italic">Aucune pièce</li>}
                {attachments.map(a => (
                  <li key={a.id} className="flex items-center justify-between gap-2">
                    <span className="text-[#0B3A2A] truncate flex-1" title={a.filename}>{a.filename}</span>
                    <span className="text-slate-400 shrink-0">{(a.size / 1024).toFixed(0)} Ko</span>
                  </li>
                ))}
              </ul>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                className="w-full" data-testid="invitation-btn-attach">
                <Upload className="w-4 h-4 mr-1.5" /> Déposer une pièce
              </Button>
              <input type="file" hidden ref={fileRef} onChange={onFile}
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" />
              <Button variant="outline" size="sm" onClick={downloadPdf}
                className="w-full mt-2" data-testid="invitation-btn-pdf">
                <FileDown className="w-4 h-4 mr-1.5" /> Télécharger la synthèse
              </Button>
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="mt-8 p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/30 text-xs text-[#0B3A2A] flex items-start gap-2" data-testid="compliance-notice">
          <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
          <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
        </div>
      </main>

      <footer className="bg-[#0B3A2A] text-white/70 mt-12 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs">
          <p className="text-[#C9A961] font-serif italic text-base mb-2">CONFILEX</p>
          <p className="max-w-3xl mx-auto leading-relaxed">{COMPLIANCE}</p>
        </div>
      </footer>
    </div>
  );
}

function TimelineItem({ item }) {
  const dt = item.at?.slice(0, 19).replace("T", " ");
  const isProposal = item.kind === "proposal";
  return (
    <li className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-2">
        <span className={`px-2 py-0.5 rounded-full border ${isProposal ? "bg-[#C9A961]/20 text-[#0B3A2A] border-[#C9A961]/40" : "bg-[#0B3A2A]/10 text-[#0B3A2A] border-[#0B3A2A]/20"}`}>
          {isProposal ? "Proposition" : "Message"}
        </span>
        <Clock className="w-3 h-3" /> {dt}
        <span className="ml-auto text-slate-500">
          {item.payload?.author?.display_name || "—"}
          {item.payload?.author?.kind && <span className="ml-1 text-slate-400">({item.payload.author.kind})</span>}
        </span>
      </div>
      {isProposal ? (
        <div className="text-sm text-[#0B3A2A]">
          <p className="font-medium mb-1">Version {item.payload.version}</p>
          {item.payload.amount_eur != null && (
            <p className="text-xs text-slate-600">
              Montant proposé : <b>{Number(item.payload.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</b>
            </p>
          )}
          {item.payload.schedule && <p className="text-xs text-slate-600">Échéancier : {item.payload.schedule}</p>}
          <p className="text-sm mt-2 whitespace-pre-wrap">{item.payload.declared_conditions}</p>
        </div>
      ) : (
        <p className="text-sm text-[#0B3A2A] whitespace-pre-wrap">{item.payload.body}</p>
      )}
    </li>
  );
}

function FullPageLoader() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#C9A961] animate-spin" />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-2xl text-[#0B3A2A] mb-3">Invitation introuvable</h1>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <Button asChild><Link to="/negociation">Découvrir CONFILEX Négociation</Link></Button>
      </div>
    </div>
  );
}
